import React, { useMemo } from 'react';
import { Table as TableComponent } from '@lucid/lego';
import type { ImporterOrder } from '../../../../api/services/lupp/order/importer/dto.importer';
import { useStore } from '../../../../store/store';
import { createOrderColumns } from './utils';

type OrdersProps = {
  theme: 'light' | 'dark';
  data: ImporterOrder[];
  totalCount: number;
  statusGroupCounts?: Record<string, number>;
  currentPage: number;
  pageSize?: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  labels: Record<string, string>;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  sortColumn?: string;
  sortDirection?: 'asc' | 'desc';
  onSort?: (column: string, direction: 'asc' | 'desc') => void;
  onStatusFilterChange?: (value: string | null) => void;
  onImporterOrderClick: (record: ImporterOrder) => void;
};

/**
 * Orders table component for the Order Desk page.
 * Displays the importer orders list with clickable IO numbers for navigation.
 * @component
 * @param props - Component props
 * @returns {JSX.Element}
 */
const Orders: React.FC<OrdersProps> = ({
  theme,
  data,
  totalCount,
  statusGroupCounts,
  currentPage,
  pageSize = 10,
  onPageChange,
  onPageSizeChange,
  labels,
  searchQuery,
  onSearchChange,
  sortColumn,
  sortDirection,
  onSort,
  onStatusFilterChange,
  onImporterOrderClick,
}) => {
  const locale = useStore((state) => state.locale);

  const columns = useMemo(
    () =>
      createOrderColumns({
        theme,
        labels,
        locale,
        onImporterOrderClick,
      }),
    [theme, labels, locale, onImporterOrderClick]
  );

  return (
    <TableComponent
      columns={columns}
      data={data}
      theme={theme === 'light' ? 'dark' : 'light'}
      selection={{ enabled: false }}
      pagination={{
        enabled: true,
        total: totalCount,
        current: currentPage,
        pageSize,
        onChange: (page) => onPageChange(page),
        onPageSizeChange,
      }}
      search={{
        placeholder: labels?.['table.searchPlaceholder'] ?? 'Search Order Number',
        value: searchQuery,
        onChange: onSearchChange,
      }}
      sort={{
        column: sortColumn,
        direction: sortDirection,
        onSort: onSort,
      }}
      columnConfig={{ enabled: true }}
      emptyState={{
        message: searchQuery.trim()
          ? (labels?.['table.noResults'] ?? 'No results found! Please update your query')
          : (labels?.['table.noOrders'] ?? 'No orders available'),
      }}
      filterCards={{
        columnKey: 'statusName',
        totalCount: statusGroupCounts?.['ALL'] ?? totalCount,
        onChange: onStatusFilterChange,
        items: [
          {
            value: 'INITIATED',
            label: labels?.['status.initiated'] ?? 'Initiated',
            count: statusGroupCounts?.['INITIATED'],
          },
          {
            value: 'PARTIALLY_FULFILLED',
            label: labels?.['status.partiallyFilled'] ?? 'Partially Fulfilled',
            count: statusGroupCounts?.['PARTIALLY_FULFILLED'],
          },
          {
            value: 'FULFILLED',
            label: labels?.['status.fulfilled'] ?? 'Fulfilled',
            count: statusGroupCounts?.['FULFILLED'],
          },
          {
            value: 'CANCELED',
            label: labels?.['status.canceled'] ?? 'Canceled',
            count: statusGroupCounts?.['CANCELED'],
          },
        ],
      }}
      labels={{
        manageColumns: labels?.['table.manageColumns'] ?? 'Manage Columns',
        showingItems:
          labels?.['table.showingItems'] ?? 'Showing {{start}}-{{end}} of {{total}} Items',
        pageOf: labels?.['table.pageOf'] ?? '{{current}} of {{total}}',
        selectAll: labels?.['table.selectAll'] ?? 'Select All',
        clearAll: labels?.['table.clearAll'] ?? 'Clear All',
        columnsVisible:
          labels?.['table.columnsVisible'] ?? '{{visible}} of {{total}} columns visible',
        save: labels?.['table.save'] ?? 'Save',
      }}
      gaComponent="orders-table"
      dataTestId="orders-table"
    />
  );
};

export default Orders;
